import { Injectable } from '@angular/core';
import { ApplicationResponse } from '../model/application-response';
import { UnemploymentApplicationStatus } from '../model/unemployment-application-status';

/**
 * Checks if the given request status indicates an incomplete request.
 * Incomplete statuses are: INCOMPLETE (3), EXTEND_DEADLINE (5), EXPECTED_EXTENDED_DEADLINE (6)
 *
 * Exported as a standalone function for use in ViewModels and other non-injectable contexts.
 */
export function isIncompleteStatus(requestState: string): boolean {
  return requestState === UnemploymentApplicationStatus.INCOMPLETE
    || requestState === UnemploymentApplicationStatus.EXTEND_DEADLINE
    || requestState === UnemploymentApplicationStatus.EXPECTED_EXTENDED_DEADLINE;
}

/**
 * Extracts unique missing document types from an application.
 * Returns a dash-separated string of document types (e.g., "C4 - C3").
 *
 * Exported as a standalone function for use in ViewModels and other non-injectable contexts.
 */
export function extractMissingDocuments(application: ApplicationResponse | null): string {
  if (!application) {
    return '';
  }
  const docTypes = application.documents?.map(doc => doc.docType?.trim()).filter(Boolean) ?? [];
  const uniqueDocTypes = Array.from(new Set(docTypes));
  return uniqueDocTypes.join(' - ');
}

/**
 * Service centralizing business logic for unemployment applications and missing documents.
 * Used by both requests and recent-infos components.
 */
@Injectable({
  providedIn: 'root'
})
export class UnemploymentApplicationService {

  /**
   * Checks if the given request status indicates an incomplete request.
   * Incomplete statuses are: INCOMPLETE (3), EXTEND_DEADLINE (5), EXPECTED_EXTENDED_DEADLINE (6)
   */
  isIncompleteStatus(requestState: string): boolean {
    return isIncompleteStatus(requestState);
  }

  /**
   * Finds the matching unemployment application for a given request and status.
   * A request is identified by its date and its sequence: a member can file two requests on the same day.
   * A request without a sequence is identified by its date alone.
   * Returns null if the status is not incomplete (3, 5, or 6).
   * This ensures that missing documents are only shown for incomplete requests.
   */
  findMatchingApplication(
    applications: ApplicationResponse[],
    targetDate: string,
    requestState: string,
    requestSequence?: number
  ): ApplicationResponse | null {
    if (!this.isIncompleteStatus(requestState)) {
      return null;
    }
    return applications.find(app =>
      app.requestDate.split(' ')[0] === targetDate
        && (requestSequence == null || app.requestSequence === requestSequence)
    ) ?? null;
  }

  /**
   * Extracts unique missing document types from an application.
   * Returns a dash-separated string of document types (e.g., "C4 - C3").
   */
  extractMissingDocuments(application: ApplicationResponse | null): string {
    return extractMissingDocuments(application);
  }

  /**
   * Extracts the application text/comment from an application.
   */
  extractApplicationText(application: ApplicationResponse | null): string {
    if (!application) {
      return '';
    }
    return application.text ?? '';
  }

  /**
   * Convenience method: finds matching application and extracts missing documents.
   */
  getMissingDocumentsForRequest(
    applications: ApplicationResponse[],
    targetDate: string,
    requestState: string,
    requestSequence?: number
  ): string {
    const application = this.findMatchingApplication(applications, targetDate, requestState, requestSequence);
    return this.extractMissingDocuments(application);
  }

  /**
   * Convenience method: finds matching application and extracts application text.
   */
  getApplicationTextForRequest(
    applications: ApplicationResponse[],
    targetDate: string,
    requestState: string,
    requestSequence?: number
  ): string {
    const application = this.findMatchingApplication(applications, targetDate, requestState, requestSequence);
    return this.extractApplicationText(application);
  }
}
